import { DatumValue } from '@nivo/core';
import { ResponsiveLineCanvas, SliceTooltipProps } from '@nivo/line';
import React from 'react';

import { CmBox, CmFlex } from '@/components/uiParts';
import { ChartDatum } from '@/domain/models/Chart';
import { CurrencyDomainService } from '@/domain/services';

export type YAxisFormat = 'currency' | 'number';

interface Props {
  data: ChartDatum[];
  yAxisFormat: YAxisFormat;
  yLegend: string;
  legendRequired: boolean;
}

const tooltip = ({ slice }: SliceTooltipProps): JSX.Element => {
  return (
    <CmBox bg="white" px={2} py={3} border="1px solid #ccc">
      <CmBox>{slice.points[0].data.xFormatted}</CmBox>
      {slice.points.map((point) => (
        <CmFlex key={point.id} color={point.serieColor} py={1}>
          <CmBox flex={1} fontWeight="bold">
            {point.serieId}
          </CmBox>
          <CmBox ml={2} color="black">
            {point.data.yFormatted}
          </CmBox>
        </CmFlex>
      ))}
    </CmBox>
  );
};

export const LineChart: React.FC<Props> = ({
  data,
  yAxisFormat,
  yLegend,
  legendRequired = true,
}) => {
  const yFormat = (value: DatumValue) => {
    switch (yAxisFormat) {
      case 'currency':
        return CurrencyDomainService.priceNotationFormat(Number(value));
      case 'number':
        return '>-,.0o';
    }
  };

  return (
    <ResponsiveLineCanvas
      data={data}
      margin={{
        top: 50,
        right: legendRequired ? 110 : 60,
        bottom: 50,
        left: 60,
      }}
      colors={{ scheme: 'category10' }}
      lineWidth={3}
      enablePoints={false}
      xScale={{
        type: 'time',
        format: '%Y-%m-%d',
        useUTC: false,
        precision: 'day',
      }}
      xFormat="time:%Y-%m-%d"
      yFormat={yFormat}
      yScale={{
        type: 'linear',
        min: 'auto',
        max: 'auto',
        stacked: true,
        reverse: false,
      }}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        format: '%Y-%m-%d',
        tickValues: 5,
      }}
      axisLeft={{
        format: (y) => {
          return CurrencyDomainService.notationFormat(Number(y));
        },
        orient: 'left',
        tickPadding: 5,
        tickRotation: 0,
        legend: yLegend,
        legendOffset: -50,
        legendPosition: 'middle',
        tickValues: Math.min(5, data[0].data.length),
      }}
      enableGridX={false}
      enableSlices="x"
      pointColor={{ from: 'color', modifiers: [] }}
      sliceTooltip={tooltip}
      legends={
        legendRequired
          ? [
              {
                anchor: 'bottom-right',
                direction: 'column',
                justify: false,
                translateX: 100,
                translateY: 0,
                itemsSpacing: 0,
                itemDirection: 'left-to-right',
                itemWidth: 80,
                itemHeight: 20,
                itemOpacity: 0.75,
                symbolSize: 12,
                symbolShape: 'circle',
                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemBackground: 'rgba(0, 0, 0, .03)',
                      itemOpacity: 1,
                    },
                  },
                ],
              },
            ]
          : undefined
      }
    />
  );
};
